import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
} from "@mui/material";
import {
  CustomDialogProps,
  SmartForm,
  SmartSubmit,
  SmartTextField,
  useSmartForm,
} from "@nodeon/react-components";
import { useSnackbar } from "notistack";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  ApiResponse,
  SecurityClassificationListDto,
  useSecurityClassificationApi,
} from "~/api/GeneratedApi";

interface FormProps {
  name: string;
}

interface Props {
  securityClassification?: SecurityClassificationListDto;
  onSubmitted?: (name: string) => void;
  initialValue?: string;
}

const SecurityClassificationDialog = ({
  handleClose,
  onSubmitted,
  securityClassification,
  initialValue,
}: CustomDialogProps & Props): JSX.Element => {
  const { editSecurityClassification, addSecurityClassification } =
    useSecurityClassificationApi();
  const { t } = useTranslation("security-classification");
  const { enqueueSnackbar } = useSnackbar();

  const edit = useMemo(
    () => !!securityClassification,
    [securityClassification],
  );

  const onSuccess = (data: FormProps, res: ApiResponse) => {
    enqueueSnackbar(t(`apiResponses:${res.resultKey}`), { variant: "success" });
    onSubmitted?.(data.name);
    handleClose?.();
  };

  const onSubmit = (data: FormProps) => {
    return edit
      ? editSecurityClassification(
          {
            name: data.name,
          },
          securityClassification!.id,
        )
      : addSecurityClassification({
          name: data.name,
        });
  };

  const defaultValues = useMemo(
    () => ({
      name: securityClassification?.name ?? initialValue ?? "",
    }),
    [initialValue, securityClassification?.name],
  );

  const formMethods = useSmartForm<FormProps, ApiResponse>({
    defaultValues,
  });

  return (
    <Dialog open onClose={handleClose}>
      <SmartForm<FormProps, ApiResponse>
        onSubmit={onSubmit}
        noGrid
        onSuccess={(data, res) => onSuccess(data, res)}
        methods={formMethods}
      >
        <DialogTitle id="form-dialog-title">
          {edit ? t("edit-title") : t("create-title")}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <SmartTextField<FormProps>
                fullWidth
                name="name"
                label={t("name")}
                required
                autoFocus
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="primary">
            {t("general:cancel")}
          </Button>
          <SmartSubmit disabled={edit ? !formMethods.formState.isDirty : false}>
            {edit ? t("general:edit") : t("general:create")}
          </SmartSubmit>
        </DialogActions>
      </SmartForm>
    </Dialog>
  );
};

export default SecurityClassificationDialog;
